import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Skeleton from '@mui/material/Skeleton';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { useDashboardTheme, ACCENT_COLOR, cardSx, panelHeaderSx, fmt, fmtBtc } from '../utils';

function fmtDuration(ms) {
  if (!ms || ms <= 0) return '—';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

function AirdropMeterPanel({ liveState, loading }) {
  const t = useDashboardTheme();
  const meter = liveState?.airdropMeter;

  if (loading && !meter) {
    return (
      <Card sx={cardSx}>
        <MDBox sx={panelHeaderSx(t)}>
          <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>Airdrop Meter</MDTypography>
        </MDBox>
        <MDBox sx={{ p: { xs: 2, sm: 3 } }}>
          <Skeleton variant='rectangular' height={80} sx={{ borderRadius: '8px' }} />
        </MDBox>
      </Card>
    );
  }

  if (!meter) {
    return (
      <Card sx={cardSx}>
        <MDBox sx={panelHeaderSx(t)}>
          <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>Airdrop Meter</MDTypography>
        </MDBox>
        <MDBox display='flex' flexDirection='column' alignItems='center' justifyContent='center' py={4} gap={1}>
          <MDTypography variant='body2' color='text' sx={{ opacity: 0.45 }}>Unavailable</MDTypography>
        </MDBox>
      </Card>
    );
  }

  const pct = Math.min(100, Math.max(0, meter.meterPercentage));
  const meterStarted = meter.meterStartTime > 0;
  const elapsed = meterStarted ? Date.now() - meter.meterStartTime : 0;

  let statusLabel = 'Inactive';
  let statusColor = 'default';
  if (meter.pendingDistribution) {
    statusLabel = 'Pending Distribution';
    statusColor = 'warning';
  } else if (meter.meterActive) {
    statusLabel = 'Active';
    statusColor = 'success';
  }

  return (
    <Card sx={cardSx}>
      <MDBox sx={panelHeaderSx(t)}>
        <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>
          Airdrop Meter
        </MDTypography>
        <Chip label={statusLabel} color={statusColor} size='small' sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
      </MDBox>

      <MDBox sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Progress bar */}
        <MDBox mb={1.5}>
          <MDBox display='flex' justifyContent='space-between' mb={0.5}>
            <MDTypography variant='caption' sx={{ color: t.secondaryText }}>
              ${fmt(meter.meterValue)} / ${fmt(meter.thresholdUsd)}
            </MDTypography>
            <MDTypography variant='caption' fontWeight='bold' sx={{ color: t.primaryText }}>
              {fmt(pct, 1)}%
            </MDTypography>
          </MDBox>
          <LinearProgress
            variant='determinate'
            value={pct}
            sx={{
              height: 10,
              borderRadius: 5,
              bgcolor: t.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              '& .MuiLinearProgress-bar': {
                background: pct >= 100
                  ? `linear-gradient(90deg, ${ACCENT_COLOR}, #c47d00)`
                  : `linear-gradient(90deg, ${ACCENT_COLOR}, rgba(226,0,27,0.6))`,
                borderRadius: 5
              }
            }}
          />
        </MDBox>

        {/* Details */}
        <MDBox display='flex' flexDirection='column' gap={0.3}>
          <MDTypography variant='caption' sx={{ color: t.secondaryText }}>
            Accumulated BTC (from blocks): <strong style={{ color: t.primaryText }}>{fmtBtc(meter.accumulatedBtc)}</strong>
          </MDTypography>
          {meterStarted && (
            <MDTypography variant='caption' sx={{ color: t.secondaryText }}>
              Cycle Duration: <strong style={{ color: t.primaryText }}>{fmtDuration(elapsed)}</strong>
            </MDTypography>
          )}
          <MDTypography variant='caption' sx={{ color: t.secondaryText }}>
            Threshold: <strong style={{ color: t.primaryText }}>${fmt(meter.thresholdUsd)}</strong>
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default AirdropMeterPanel;
